/**
 * DAZE Golf — Meta 广告落地页指定产品置顶
 * URL 格式：/collections/best-sellers?pin=handle-1,handle-2,handle-3
 * 适配 Shopify Horizon/Fabric 商品网格（.product-grid > .product-grid__item）。
 * 只在 BS 集合第一页且提供 pin 参数时启用，不改动 Shopify 后台的集合排序。
 * 将第二页产品的现有完整 HTML 卡片搬到第一页，避免重建卡片而丢失标签、价格与评价。
 * 注意：这属于浏览器端置顶；被跨页搬来的产品仍属于原本分页，可能在后页重复出现。
 */
(() => {
  'use strict';

  if (!/\/collections\/best-sellers\/?$/.test(window.location.pathname)) return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('page') && params.get('page') !== '1') return;

  const handles = [...new Set(
    (params.get('pin') || '')
      .split(',')
      .map(s => s.trim().toLowerCase())
      .filter(s => /^[a-z0-9][a-z0-9-]*$/.test(s))
  )].slice(0, 12);
  if (!handles.length) return;

  const handleSet = new Set(handles);
  const cardSelector = ':scope > li.product-grid__item';
  const gridSelector = 'ul.product-grid[data-testid="product-grid"], ul.product-grid';
  const remoteCards = new Map();
  let running = false;
  let loadedRemote = false;
  let rerunTimer;

  function cardsOf(grid) {
    return Array.from(grid.querySelectorAll(cardSelector));
  }

  function cardHandle(card) {
    for (const a of card.querySelectorAll('a[href*="/products/"]')) {
      try {
        const pathname = new URL(a.getAttribute('href'), location.origin).pathname;
        const match = pathname.match(/\/products\/([^/?#]+)/);
        if (match) return decodeURIComponent(match[1]).toLowerCase();
      } catch (_) { /* 继续检查其它链接 */ }
    }
    return null;
  }

  async function findAcrossPages(grid, found) {
    if (loadedRemote) return;
    loadedRemote = true;
    // 使用 Shopify 网格的总页数；最多请求 5 页，避免大型集合额外加载太多页面。
    const total = Math.min(Math.max(parseInt(grid.getAttribute('data-last-page') || '2', 10) || 2, 1), 5);
    for (let page = 2; page <= total; page++) {
      if (handles.every(h => found.has(h) || remoteCards.has(h))) break;
      const url = new URL(location.href);
      url.searchParams.delete('pin');
      url.searchParams.set('page', String(page));
      try {
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) continue;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = doc.querySelector(gridSelector);
        if (!nextGrid) continue;
        cardsOf(nextGrid).forEach(card => {
          const h = cardHandle(card);
          if (handleSet.has(h) && !remoteCards.has(h)) remoteCards.set(h, card);
        });
      } catch (error) {
        console.warn('[DAZE Pin] 读取集合第 ' + page + ' 页失败', error);
      }
    }
  }

  async function applyPins() {
    if (running) return;
    const grid = document.querySelector(gridSelector);
    if (!grid) return;
    const originalCards = cardsOf(grid);
    if (!originalCards.length) return;

    // 已经按要求排序时不再改动 DOM，避免与主题的动态更新产生循环。
    if (handles.every((handle, i) => cardHandle(originalCards[i]) === handle)) return;
    running = true;
    try {
      const available = new Map();
      originalCards.forEach(card => {
        const h = cardHandle(card);
        if (handleSet.has(h) && !available.has(h)) available.set(h, card);
      });

      if (handles.some(h => !available.has(h))) await findAcrossPages(grid, available);
      if (!grid.isConnected) return;

      const ordered = handles.map(h => available.get(h) || remoteCards.get(h)).filter(Boolean);
      // 先检查已找到的产品是否已经正确排在最前面，防止动态监听造成重复移动。
      const currentFirst = cardsOf(grid).slice(0, ordered.length).map(cardHandle);
      const wantedFirst = ordered.map(cardHandle);
      if (ordered.length && wantedFirst.some((h, i) => currentFirst[i] !== h)) {
        // 从后往前插入，保持 Excel 输入顺序。
        for (let i = ordered.length - 1; i >= 0; i--) {
          grid.insertBefore(ordered[i], grid.firstChild);
        }
      }
      const missing = handles.filter(h => !available.has(h) && !remoteCards.has(h));
      if (missing.length) console.warn('[DAZE Pin] 未在集合前五页找到这些 Handle：', missing);
    } finally {
      running = false;
    }
  }

  function schedule() {
    clearTimeout(rerunTimer);
    rerunTimer = setTimeout(() => applyPins().catch(console.error), 180);
  }

  function start() {
    schedule();
    // Fabric 主题过滤 / 翻页可能局部替换商品网格，检测后重新应用。
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
